import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin, adminGetBookings, adminGetCandidates, adminGetSlots, adminUpdateSlot, adminBulkUpdateSlots, fetchPrograms, fetchCourses, getAccompaniment, getUserPurchasedPrograms, adminGetCandidatePayments } from '../services/api';
import { Lock, CalendarDays, Users, Clock, BookOpen, PhoneCall } from 'lucide-react';
import { toast } from 'sonner';
import AdminAgendaTab from '../components/admin/AgendaTab';
import AdminSlotsTab from '../components/admin/SlotsTab';
import AdminFormationsTab from '../components/admin/FormationsTab';
import AdminCandidatesTab from '../components/admin/CandidatesTab';
import RelanceTab from '../components/admin/RelanceTab';

const TIME_SLOTS = (() => {
  const slots = [];
  for (let h = 9; h < 19; h++) {
    for (let m = 0; m < 60; m += 15) {
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
    }
  }
  return slots;
})();

const AdminPage = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('agenda');
  const [bookings, setBookings] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [adminSlots, setAdminSlots] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [expandedProgram, setExpandedProgram] = useState(null);
  const [loading, setLoading] = useState(false);

  // Candidate detail state
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidateAccompaniment, setCandidateAccompaniment] = useState(null);
  const [candidatePurchases, setCandidatePurchases] = useState([]);
  const [candidatePayments, setCandidatePayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentForm, setPaymentForm] = useState({ programId: '', formula: '', programName: '', amount: 0, paymentMethod: 'virement', nbInstallments: 3, firstDate: '', installmentDates: [] });
  const [savingAccompaniment, setSavingAccompaniment] = useState(false);
  const [showCreateCandidate, setShowCreateCandidate] = useState(false);
  const [creatingCandidate, setCreatingCandidate] = useState(false);
  const [newCandidate, setNewCandidate] = useState({ name: '', email: '', password: '', phone: '', address: '', programId: '', formula: 'bronze' });
  const [candidateNotes, setCandidateNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  // Agenda state
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1); // Monday
    d.setHours(0, 0, 0, 0);
    return d;
  });

  // Slot management state
  const [slotDate, setSlotDate] = useState(null);
  const [customTime, setCustomTime] = useState('08:00');
  const [customLabel, setCustomLabel] = useState('');

  // Auto-login if token exists
  useEffect(() => {
    if (token) {
      adminGetBookings(token)
        .then(() => setIsLoggedIn(true))
        .catch(() => { setToken(''); localStorage.removeItem('adminToken'); });
    }
  }, []);

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [b, c, s, p, co] = await Promise.all([
        adminGetBookings(token),
        adminGetCandidates(token),
        adminGetSlots(token),
        fetchPrograms(),
        fetchCourses()
      ]);
      setBookings(b);
      setCandidates(c);
      setAdminSlots(s);
      setPrograms(p);
      setCourses(co);
    } catch (e) {
      toast.error('Erreur de chargement des données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) loadData();
  }, [isLoggedIn]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const result = await adminLogin(email, password);
      setToken(result.access_token);
      localStorage.setItem('adminToken', result.access_token);
      setIsLoggedIn(true);
      toast.success('Bienvenue Blandine !');
    } catch {
      toast.error('Identifiants invalides');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setIsLoggedIn(false);
    localStorage.removeItem('adminToken');
  };

  // Week navigation
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(currentWeekStart);
      d.setDate(d.getDate() + i);
      days.push(d);
    }
    return days;
  }, [currentWeekStart]);

  const prevWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() - 7);
    setCurrentWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() + 7);
    setCurrentWeekStart(d);
  };
  const goToToday = () => {
    const d = new Date();
    d.setDate(d.getDate() - d.getDay() + 1);
    d.setHours(0, 0, 0, 0);
    setCurrentWeekStart(d);
  };

  // Build agenda data
  const blockedSet = useMemo(() => {
    const s = new Set();
    adminSlots.filter(sl => sl.blocked).forEach(sl => s.add(`${sl.date}|${sl.time}`));
    return s;
  }, [adminSlots]);

  const bookingsByDatetime = useMemo(() => {
    const map = {};
    bookings.forEach(b => {
      const key = `${b.date}|${b.time}`;
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [bookings]);

  const toggleSlotBlock = async (dateStr, time) => {
    const key = `${dateStr}|${time}`;
    const isCurrentlyBlocked = blockedSet.has(key);
    try {
      await adminUpdateSlot(token, dateStr, time, !isCurrentlyBlocked);
      // Update local state
      setAdminSlots(prev => {
        const filtered = prev.filter(s => !(s.date === dateStr && s.time === time));
        filtered.push({ date: dateStr, time, blocked: !isCurrentlyBlocked });
        return filtered;
      });
      toast.success(isCurrentlyBlocked ? 'Créneau ouvert' : 'Créneau bloqué');
    } catch {
      toast.error('Erreur');
    }
  };

  const blockEntireDay = async (dateStr) => {
    try {
      const slots = TIME_SLOTS.map(time => ({ date: dateStr, time, blocked: true }));
      await adminBulkUpdateSlots(token, slots);
      setAdminSlots(prev => {
        const filtered = prev.filter(s => s.date !== dateStr);
        return [...filtered, ...slots];
      });
      toast.success('Journée bloquée');
    } catch {
      toast.error('Erreur');
    }
  };

  const openEntireDay = async (dateStr) => {
    try {
      const slots = TIME_SLOTS.map(time => ({ date: dateStr, time, blocked: false }));
      await adminBulkUpdateSlots(token, slots);
      setAdminSlots(prev => {
        const filtered = prev.filter(s => s.date !== dateStr);
        return [...filtered, ...slots];
      });
      toast.success('Journée ouverte');
    } catch {
      toast.error('Erreur');
    }
  };

  // Login screen
  if (!isLoggedIn) {
    return (
      <div style={{ paddingTop: '80px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="card" style={{ maxWidth: '400px', width: '100%', padding: '2.5rem' }} data-testid="admin-login-form">
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: 'linear-gradient(135deg, #E75480, #C2185B)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
              <Lock size={28} style={{ color: 'white' }} />
            </div>
            <h1 className="heading-2" style={{ marginBottom: '0.5rem' }}>Espace Blandine</h1>
            <p className="body-small" style={{ color: 'var(--text-muted)' }}>Connectez-vous pour accéder à l'administration</p>
          </div>
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: '1rem' }}>
              <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Email</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="apdr.vae@gmail.com" data-testid="admin-email"
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', border: '2px solid var(--border-primary)', background: 'var(--bg-card)', fontSize: '0.9rem' }} />
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label className="body-small" style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 600 }}>Mot de passe</label>
              <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-password"
                style={{ width: '100%', padding: '0.75rem 1rem', borderRadius: '10px', border: '2px solid var(--border-primary)', background: 'var(--bg-card)', fontSize: '0.9rem' }} />
            </div>
            <button type="submit" className="btn-primary" disabled={loginLoading} data-testid="admin-login-btn"
              style={{ width: '100%', justifyContent: 'center' }}>
              {loginLoading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const formatDate = (d) => d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  const formatDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const isToday = (d) => {
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  };

  return (
    <div style={{ paddingTop: '80px', minHeight: '100vh' }}>
      {/* Header */}
      <section style={{ background: 'linear-gradient(135deg, #E75480 0%, #C2185B 100%)', padding: '2rem 1.2rem', color: 'white' }}>
        <div className="container" style={{ maxWidth: '1200px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.25rem' }}>Espace Blandine</h1>
            <p style={{ opacity: 0.85, fontSize: '0.85rem' }}>Gestion des réservations et disponibilités</p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button onClick={() => navigate('/dashboard')} style={{ background: 'rgba(255,255,255,0.9)', border: 'none', color: '#C2185B', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }} data-testid="admin-go-dashboard">
              Voir les formations
            </button>
            <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }} data-testid="admin-logout">
              Déconnexion
            </button>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <div className="container" style={{ maxWidth: '1200px', padding: '1.5rem 1.2rem 0' }}>
        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '2px solid var(--border-light)', paddingBottom: '0' }}>
          {[
            { id: 'agenda', label: 'Agenda', icon: CalendarDays },
            { id: 'slots', label: 'Gérer créneaux', icon: Clock },
            { id: 'formations', label: 'Formations', icon: BookOpen },
            { id: 'relance', label: 'Relance', icon: PhoneCall },
            { id: 'candidates', label: 'Candidats', icon: Users }
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem 1.25rem',
                border: 'none', background: 'none', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600,
                color: activeTab === tab.id ? '#E75480' : 'var(--text-muted)',
                borderBottom: activeTab === tab.id ? '3px solid #E75480' : '3px solid transparent',
                marginBottom: '-2px', transition: 'all 0.2s'
              }}>
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="container" style={{ maxWidth: '1200px', padding: '2rem 1.2rem' }}>
        {loading && <p style={{ textAlign: 'center', color: 'var(--text-muted)' }}>Chargement...</p>}

        {/* AGENDA TAB */}
        {activeTab === 'agenda' && !loading && (
          <AdminAgendaTab
            currentWeekStart={currentWeekStart}
            weekDays={weekDays}
            prevWeek={prevWeek}
            nextWeek={nextWeek}
            goToToday={goToToday}
            bookingsByDatetime={bookingsByDatetime}
            blockedSet={blockedSet}
            toggleSlotBlock={toggleSlotBlock}
            blockEntireDay={blockEntireDay}
            openEntireDay={openEntireDay}
            bookings={bookings}
            setBookings={setBookings}
            TIME_SLOTS={TIME_SLOTS}
            formatDate={formatDate}
            formatDateStr={formatDateStr}
            isToday={isToday}
            token={token}
            adminSlots={adminSlots}
          />
        )}

        {/* SLOTS MANAGEMENT TAB */}
        {activeTab === 'slots' && !loading && (
          <AdminSlotsTab
            slotDate={slotDate}
            setSlotDate={setSlotDate}
            customTime={customTime}
            setCustomTime={setCustomTime}
            customLabel={customLabel}
            setCustomLabel={setCustomLabel}
            token={token}
            adminSlots={adminSlots}
            setAdminSlots={setAdminSlots}
            TIME_SLOTS={TIME_SLOTS}
            blockedSet={blockedSet}
            bookingsByDatetime={bookingsByDatetime}
            blockEntireDay={blockEntireDay}
            openEntireDay={openEntireDay}
            toggleSlotBlock={toggleSlotBlock}
          />
        )}

        {/* FORMATIONS TAB */}
        {activeTab === 'formations' && !loading && (
          <AdminFormationsTab
            programs={programs}
            courses={courses}
            expandedProgram={expandedProgram}
            setExpandedProgram={setExpandedProgram}
          />
        )}

        {/* RELANCE TAB */}
        {activeTab === 'relance' && !loading && (
          <RelanceTab token={token} />
        )}

        {/* CANDIDATES TAB */}
        {activeTab === 'candidates' && !loading && (
          <AdminCandidatesTab
            candidates={candidates}
            setCandidates={setCandidates}
            programs={programs}
            token={token}
            loadData={loadData}
            selectedCandidate={selectedCandidate}
            setSelectedCandidate={setSelectedCandidate}
            candidateAccompaniment={candidateAccompaniment}
            setCandidateAccompaniment={setCandidateAccompaniment}
            candidatePurchases={candidatePurchases}
            setCandidatePurchases={setCandidatePurchases}
            candidatePayments={candidatePayments}
            setCandidatePayments={setCandidatePayments}
            showPaymentForm={showPaymentForm}
            setShowPaymentForm={setShowPaymentForm}
            paymentForm={paymentForm}
            setPaymentForm={setPaymentForm}
            savingAccompaniment={savingAccompaniment}
            setSavingAccompaniment={setSavingAccompaniment}
            showCreateCandidate={showCreateCandidate}
            setShowCreateCandidate={setShowCreateCandidate}
            creatingCandidate={creatingCandidate}
            setCreatingCandidate={setCreatingCandidate}
            newCandidate={newCandidate}
            setNewCandidate={setNewCandidate}
            candidateNotes={candidateNotes}
            setCandidateNotes={setCandidateNotes}
            savingNotes={savingNotes}
            setSavingNotes={setSavingNotes}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPage;
